import { BehaviorSubject, Observable, Subscription, EMPTY, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  ignoreElements,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { EditorFormValue } from './editor-domain.models';
import {
  EditorBackendPreviewPlan,
  EditorBackendPreviewState,
  buildBackendPreviewPlan,
  createInitialEditorBackendPreviewState,
  createPreviewRequestError,
} from './editor-preview.adapter';
import { EditImageRequest } from './editor-request.adapter';

export interface EditorBackendPreviewOrchestratorOptions {
  requestPreview: (request: EditImageRequest) => Observable<Blob>;
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (url: string) => void;
  debounceMs?: number;
}

export class EditorBackendPreviewOrchestrator {
  readonly #stateSubject = new BehaviorSubject<EditorBackendPreviewState>(
    createInitialEditorBackendPreviewState(),
  );

  readonly state$ = this.#stateSubject.asObservable();

  readonly #requestPreview: (request: EditImageRequest) => Observable<Blob>;
  readonly #createObjectUrl: (blob: Blob) => string;
  readonly #revokeObjectUrl: (url: string) => void;
  readonly #debounceMs: number;

  #subscription: Subscription | null = null;
  #objectUrl: string | null = null;
  #activeRequestKey: string | null = null;

  constructor(options: EditorBackendPreviewOrchestratorOptions) {
    this.#requestPreview = options.requestPreview;
    this.#createObjectUrl = options.createObjectUrl ?? (blob => URL.createObjectURL(blob));
    this.#revokeObjectUrl = options.revokeObjectUrl ?? (url => URL.revokeObjectURL(url));
    this.#debounceMs = options.debounceMs ?? 250;
  }

  connect(source$: Observable<EditorFormValue>): void {
    this.disconnect();

    this.#subscription = source$
      .pipe(
        map(value => buildBackendPreviewPlan(value)),
        distinctUntilChanged((left, right) => areEqualPlans(left, right)),
        switchMap(plan => this.runPlan(plan)),
      )
      .subscribe();
  }

  disconnect(): void {
    this.#subscription?.unsubscribe();
    this.#subscription = null;
    this.#activeRequestKey = null;
  }

  destroy(): void {
    this.disconnect();
    this.#activeRequestKey = null;
    this.clearObjectUrl();
    this.#stateSubject.next(createInitialEditorBackendPreviewState());
  }

  snapshot(): EditorBackendPreviewState {
    return this.#stateSubject.getValue();
  }

  private runPlan(plan: EditorBackendPreviewPlan): Observable<never> {
    if (plan.status === 'idle') {
      this.#activeRequestKey = null;
      this.clearObjectUrl();
      this.#stateSubject.next(createInitialEditorBackendPreviewState());
      return EMPTY;
    }

    if (plan.status === 'unsupported') {
      this.#activeRequestKey = null;
      this.clearObjectUrl();
      this.#stateSubject.next({
        status: 'error',
        scope: plan.scope,
        objectUrl: null,
        error: plan.error,
        requestKey: plan.requestKey,
      });
      return EMPTY;
    }

    this.#activeRequestKey = plan.requestKey;
    this.#stateSubject.next({
      status: 'loading',
      scope: plan.scope,
      objectUrl: this.#objectUrl,
      error: null,
      requestKey: plan.requestKey,
    });

    return timer(this.#debounceMs).pipe(
      switchMap(() =>
        this.#requestPreview(plan.request).pipe(
          take(1),
          tap(resultBlob => {
            if (this.#activeRequestKey !== plan.requestKey) {
              return;
            }

            const nextObjectUrl = this.#createObjectUrl(resultBlob);
            this.replaceObjectUrl(nextObjectUrl);
            this.#stateSubject.next({
              status: 'ready',
              scope: plan.scope,
              objectUrl: nextObjectUrl,
              error: null,
              requestKey: plan.requestKey,
            });
          }),
          catchError(error => {
            if (this.#activeRequestKey !== plan.requestKey) {
              return EMPTY;
            }

            this.clearObjectUrl();
            this.#stateSubject.next({
              status: 'error',
              scope: plan.scope,
              objectUrl: null,
              error: createPreviewRequestError(error),
              requestKey: plan.requestKey,
            });
            return EMPTY;
          }),
          ignoreElements(),
        ),
      ),
    );
  }

  private replaceObjectUrl(nextObjectUrl: string): void {
    if (this.#objectUrl && this.#objectUrl !== nextObjectUrl) {
      this.#revokeObjectUrl(this.#objectUrl);
    }

    this.#objectUrl = nextObjectUrl;
  }

  private clearObjectUrl(): void {
    if (!this.#objectUrl) {
      return;
    }

    this.#revokeObjectUrl(this.#objectUrl);
    this.#objectUrl = null;
  }
}

function areEqualPlans(
  left: EditorBackendPreviewPlan,
  right: EditorBackendPreviewPlan,
): boolean {
  if (left.status !== right.status || left.scope !== right.scope) {
    return false;
  }

  switch (left.status) {
    case 'idle':
      return true;
    case 'request':
      return right.status === 'request' && left.requestKey === right.requestKey;
    case 'unsupported':
      return (
        right.status === 'unsupported' &&
        left.requestKey === right.requestKey &&
        left.error.Details === right.error.Details
      );
  }
}
